import { connect } from "react-redux";
import { withRouter } from "react-router";
import { createSelector } from "reselect";
import { Maybe } from "true-myth";

import SchedulingPage from "components/schedule/scheduling_page";

import { fetchSalesForecast, fetchDayParts } from "actions/account";
import { clearFilters } from "actions/scheduling/filters";
import {
  fetchDraftSchedule,
  fetchPublishedSchedule,
  fetchPosUsers,
  fetchUnavail,
  autoSaveDraft,
  cancelAutoSaveDraft,
  setScheduleDateBoundaries,
  SchedulingActionTypes,
  publishSchedule,
  notifyPosUsers,
  toggleNotifyModal,
  MODAL_STATUS,
} from "actions/scheduling";
import { fetchTemplates } from "actions/templates";
import { NotifyType } from "helpers/schedule/scheduling_page";
import { selectPrettyURL } from "store/select/account";
import {
  selectPublicURL,
  selectDateParams,
  selectRawSchedule,
  selectRoutePermalink,
  selectScheduling,
  selectOrderedSchedule,
  selectFilters,
} from "store/select/scheduling";
import { selectFeatureFlags } from "store/select/context";
import { IRootStateProps } from "types/reducers";
import { RequestStatus } from "types/requests";
import { ISchedule, IPeople } from "types/schedules";

interface IPropsFromState {
  dayView: Maybe<Date>;
  endDate: Maybe<Date>;
  filters: IRootStateProps["filters"];
  interactiveView: boolean;
  locationsRequestStatus: RequestStatus;
  orderedSchedule: ISchedule;
  permalink: Maybe<string>;
  publicURL: string;
  publishedSchedule: Maybe<ISchedule>;
  rawSchedule: ISchedule;
  requestStatuses: IRootStateProps["scheduling"]["requestStatuses"];
  showNotifyScheduleModal: MODAL_STATUS;
  startDate: Maybe<Date>;
  storePrettyUrl: string;
}

interface IMapDispatchToProps {
  fetchDayParts: (storePrettyUrl: string) => void;
  fetchDraftSchedule: (
    startDate: Date,
    endDate: Date,
    location: string
  ) => void;
  fetchPosUsers: (location: string, prettyUrl: string) => void;
  fetchPublishedSchedule: (
    startDate: Date,
    endDate: Date,
    location: string,
    forPublic: boolean
  ) => void;
  fetchSalesForecast: (storePrettyUrl: string) => void;
  fetchTemplates: (location: string) => void;
  fetchUnavail: (location: string) => void;
  autoSaveDraft: () => void;
  cancelAutoSaveDraft: () => void;
  clearFilters: () => void;
  publishSchedule: () => void;
  notifyPosUsers: (
    location: string,
    startDate: Date,
    endDate: Date,
    people: IPeople,
    notifyType: NotifyType
  ) => void;
  setScheduleDateBoundaries: (
    startDate: Date,
    endDate: Date
  ) => SchedulingActionTypes;
  toggleNotifyModal: (status: MODAL_STATUS) => void;
}

const mapStateToPropsSelector = createSelector(
  selectDateParams,
  selectFilters,
  selectOrderedSchedule,
  selectRawSchedule,
  selectPrettyURL,
  selectPublicURL,
  selectRoutePermalink,
  selectScheduling,
  selectFeatureFlags,
  (
    { startDate, endDate, dayView },
    filters,
    orderedSchedule,
    rawSchedule,
    { storePrettyUrl, locationsRequestStatus },
    publicURL,
    permalink,
    { published, requestStatuses, showNotifyScheduleModal }
  ): IPropsFromState => {
    return {
      dayView,
      endDate,
      filters,
      interactiveView: true,
      locationsRequestStatus,
      orderedSchedule,
      permalink,
      publicURL,
      publishedSchedule: published,
      rawSchedule,
      requestStatuses,
      showNotifyScheduleModal,
      startDate,
      storePrettyUrl,
    };
  }
);

const mapDispatchToProps: IMapDispatchToProps = {
  autoSaveDraft,
  cancelAutoSaveDraft,
  clearFilters,
  fetchDayParts,
  fetchDraftSchedule,
  fetchPosUsers,
  fetchPublishedSchedule,
  fetchSalesForecast,
  fetchTemplates,
  fetchUnavail,
  notifyPosUsers,
  publishSchedule,
  setScheduleDateBoundaries,
  toggleNotifyModal,
};

const ScheduleContainer = withRouter(
  connect(mapStateToPropsSelector, mapDispatchToProps)(SchedulingPage)
);

export default ScheduleContainer;
